// CRM C-II: Platzhalter in Vorlagen und Textbausteinen.
//
// Aufgelöst wird **beim Einfügen**, nicht beim Speichern — sonst ist die Vorlage nach
// einmaligem Gebrauch aufgebraucht.
//
// Die härteste Regel steht nicht im Satz der Platzhalter, sondern im Umgang mit dem,
// was fehlt: **ein unaufgelöster Platzhalter darf nie als `{{…}}` in einer Kundenmail
// landen.** Er wird durch eine sichtbare Lücke `[[name]]` ersetzt — dort, wo der Wert
// gestanden hätte, damit der Absender die Stelle findet und nicht nur erfährt, dass es
// eine gibt. Das Compose-Fenster sperrt den Versand, solange eine Lücke im Text steht;
// Leerstring wäre die stille Variante gewesen und ging in der Mail unter. Bei den
// Anrede-Platzhaltern ist das der häufigere Fall — `{{formalGreeting}}` ist bei vier von
// fünf Kontakten leer (2.119 von 11.011).
//
// Eigenes Modul mit Tests, weil ein Fehler hier nicht auffällt, sondern in einer Mail
// beim Kunden landet.

// Aus `salutation` ("Herr", "Frau", "Frau Dr") oder ersatzweise `gender` (m/w), plus
// Nachname. Kein Rückfall auf eine neutrale Form: eine falsche Anrede ist schlimmer
// als keine, und die Warnung sagt dem Absender, dass er selbst formulieren muss.
//
// `salutation` hat Vorrang, weil nur dort der Titel steht: "Frau Dr" ergibt "Sehr
// geehrte Frau Dr. Müller", `gender` allein könnte den Doktor nur verschlucken.
// Entschieden wird am ersten Wort — "Herr" männlich, "Frau" weiblich; was dahinter
// steht, wird unverändert übernommen und nicht gedeutet.
const formalGreeting = (contact) => {
  const lastName = String(contact?.lastName ?? "").trim();
  if (!lastName) return "";

  // "Frau Dr" -> "Frau Dr." : ein abgekürzter Titel ohne Punkt sieht in einer
  // Kundenmail nach Tippfehler aus. Nur bei den gängigen Abkürzungen, und nur am
  // Ende — "Dr. med." trägt seinen Punkt schon.
  const salutation = String(contact?.salutation ?? "")
    .trim()
    .replace(/\b(Dr|Prof|Ing|Dipl)$/i, "$1.");
  const [first = ""] = salutation.split(/\s+/);
  if (/^herr$/i.test(first)) return `Sehr geehrter ${salutation} ${lastName}`;
  if (/^frau$/i.test(first)) return `Sehr geehrte ${salutation} ${lastName}`;

  const gender = String(contact?.gender ?? "").trim().toLowerCase();
  if (gender === "m") return `Sehr geehrter Herr ${lastName}`;
  if (gender === "w") return `Sehr geehrte Frau ${lastName}`;
  return "";
};

/**
 * @param context { company, contact, sender }
 * @returns Map Platzhaltername -> Wert (Leerstring, wenn nicht auflösbar)
 */
export const placeholderValues = ({ company, contact, sender } = {}) => ({
  companyName: String(company?.name ?? "").trim(),
  // " " ist der Sentinel für "keine Nummer" (Leads haben keine) — er darf nicht als
  // Leerzeichen in der Mail landen.
  companyNumber: String(company?.number ?? "").trim(),
  contactFirstName: String(contact?.firstName ?? "").trim(),
  contactLastName: String(contact?.lastName ?? "").trim(),
  senderFirstName: String(sender?.firstName ?? "").trim(),
  senderLastName: String(sender?.lastName ?? "").trim(),
  // A-VII Teil 3: die drei Anrede-Felder aus HubSpot.
  salutation: String(contact?.salutation ?? "").trim(),
  formalGreeting: formalGreeting(contact),
  personalGreeting: String(contact?.personalSalutation ?? "").trim(),
});

const PATTERN = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;

/**
 * Die Lücke im Text. Bewusst `[[…]]` und nicht `{{…}}`: so ist an der Schreibweise
 * ablesbar, ob ein Platzhalter noch aufzulösen ist oder schon aufgegeben wurde — und
 * die Prüfung vor dem Senden verwechselt beide nicht.
 */
export const gapFor = (name) => `[[${name}]]`;

const GAP_PATTERN = /\[\[([a-zA-Z]+)\]\]/g;

/** Offene Lücken in Betreff und Text — jede nur einmal, in Reihenfolge des Auftretens. */
export const gapsIn = (text) => [
  ...new Set(
    [...String(text ?? "").matchAll(GAP_PATTERN)].map((match) => match[1])
  ),
];

/**
 * Ersetzt alle Platzhalter. Unbekannte oder leere werden zur sichtbaren Lücke
 * `[[name]]` und stehen in `missing`.
 *
 * @returns { text, missing: string[] }
 */
export const resolvePlaceholders = (template, context) => {
  const values = placeholderValues(context);
  const missing = new Set();
  const text = String(template ?? "").replace(PATTERN, (_match, name) => {
    const value = values[name];
    if (!value) {
      missing.add(name);
      return gapFor(name);
    }
    return value;
  });
  return { text, missing: [...missing] };
};
